import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SearchService } from './service/search-bar.service';
import { Search, SearchProductResponse } from './interfaces/search-bar.interface';
import { Product } from '../../../features/establishment-main/components/products/interfaces/products.interface';
import { CreateSlugService } from '../../../core/service/create-slug.service';
import { Router } from '@angular/router';
import { ProductModalService } from '../../../core/service/product-modal.service';
import { SearchBarAdapterService, Establishment } from './adapters/search-bar-adapter.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'shared-search-bar',
  imports: [CommonModule, FormsModule],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.css',
})
export class SearchBarComponent implements OnInit, AfterViewInit, OnDestroy {
  isActive = signal(false);
  readonly defaultImage = 'assets/imagen_default.png';

  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('searchContainer', { static: false }) searchContainer!: ElementRef<HTMLElement>;

  // Variables para la búsqueda
  searchTerm = signal('');
  showResults = signal(false);

  isLoading = signal(false);
  establishments = signal<Establishment[]>([]);
  products = signal<Product[]>([]);
  filteredResults = signal(false);
  selectedEstablishment = signal<Establishment | null>(null);
  showingEstablishmentProducts = signal(false);

  private searchBarAdapter: SearchBarAdapterService = inject(SearchBarAdapterService);

  constructor(
    private searchService: SearchService,
    private createSlugService: CreateSlugService,
    private router: Router,
    private productModalService: ProductModalService
  ) {
    toObservable(this.searchTerm)
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(term => term.length >= 1),
        tap(() => this.isLoading.set(true)),
        switchMap(term => this.searchService.search(term).pipe(
          catchError(error => {
            console.error('Error en la búsqueda:', error);
            this.isLoading.set(false);
            return of(null);
          })
        ))
      )
      .subscribe((response) => {
        if (response) {
          this.establishments.set(response.data.restaurants.map(this.searchBarAdapter.rawEstablishmentToEstablishment as any));
          this.products.set(response.data.products.map(this.searchBarAdapter.rawProductToProduct));
          this.showResults.set(true);
        }
        this.isLoading.set(false);
      });
  }

  ngOnInit(): void {
    // Initialization logic if needed
  }

  ngAfterViewInit(): void {
    // ViewChild references are now available
    // Any initialization that requires DOM elements can be done here
  }

  ngOnDestroy(): void {
    // Clean up any resources if needed
  }

  toggleActive(event: Event): void {
    event.stopPropagation();
    this.isActive.set(true);

    // Si hay texto en el campo de búsqueda, expandir automáticamente
    if (this.searchTerm() && this.searchTerm().length >= 1) {
      this.onSearchInput({ target: { value: this.searchTerm() } });
    }
  }

  onSearchInput(event: any): void {
    const value = event.target.value;
    this.searchTerm.set(value);
    this.showingEstablishmentProducts.set(false);
    this.selectedEstablishment.set(null);

    // Solo realizar búsqueda si hay 1 o más caracteres
    if (value.length < 2) {
      this.showResults.set(false);
    }
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchTerm.set('');
    this.showResults.set(false);
    this.establishments.set([]);
    this.products.set([]);
    this.selectedEstablishment.set(null);
    this.showingEstablishmentProducts.set(false);

    // Usar ViewChild en lugar de querySelector
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      // Solo mantener el foco si el evento no vino del overlay
      if (!(event.target as HTMLElement).classList.contains('search-overlay')) {
        this.searchInput.nativeElement.focus();
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Usar ViewChild en lugar de la referencia directa al DOM
    if (this.searchContainer?.nativeElement && !this.searchContainer.nativeElement.contains(event.target as Node)) {
      this.isActive.set(false);
      this.showResults.set(false);
    }
  }

  onImageError(event: any): void {
    event.target.src = this.defaultImage;
  }

  navigateToProducts(restaurantId: number, restaurantName: string): void {
    const slug = this.createSlugService.createSlug(restaurantName);

    // Cerrar el panel de búsqueda al navegar
    this.showResults.set(false);
    this.isActive.set(false);

    // Limpiar el texto de búsqueda
    this.searchTerm.set('');
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }

    this.router.navigate(['/products-establishment', restaurantId, slug]);
  }

  backToGeneralSearch(): void {
    this.showingEstablishmentProducts.set(false);
    this.selectedEstablishment.set(null);
    if (this.searchTerm().length >= 1) {
      // Trigger search again or just show previous results if they were cached?
      // Since we are reactive, setting the same value might not trigger distinctUntilChanged if we just call set.
      // But here we want to restore the view.
      // Actually, if we just hide establishment products, the main list should be there if we didn't clear it.
      // And we didn't clear establishments/products signals.
      this.showResults.set(true);
    }
  }
  openProductModal(product: Product) {
    // console.log('mandndo producto',product);

    this.productModalService.openModal(product);
  }
}
